import axios from 'axios'
import React, { useState, useContext } from 'react'
import Button from '../../../button/Button'
import { baseApiURL } from 'src/utils/utils'
import Input from '../../../input/Input'
import { IState } from 'src/Interface'
import { StateContext } from '../../../../AppContext'
import styles from './add-attribute.module.scss'
import { toast } from 'react-toastify'
import { validString } from 'src/utils/utils'
import { DispatchContext } from 'src/AppContext'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface IValues {
  description?: string
  code?: string
  globalId?: number
}

interface IErrors {
  description?: boolean
  code?: boolean
  globalId?: boolean
}

interface Props {
  close: () => void
}

const AddAttribute: React.FunctionComponent<Props> = ({ close }: Props) => {
  const state: IState = useContext(StateContext)
  const dispatch = useContext(DispatchContext)
  const queryClient = useQueryClient()
  const [values, setValues] = useState<Array<IValues>>(JSON.parse(window.sessionStorage.getItem('attributes')) || [{
    description: '',
    code: '',
    globalId: undefined
  }])

  const [validationErrors, setValidationErrors] = useState<Array<IErrors>>([{
    description: false,
    code: false,
    globalId: false
  }])

  const [errors, setErrors] = useState<Array<IErrors>>([{
    description: false,
    code: false,
    globalId: false
  }])

  const validCode = /^.{2,3}$/
  const validId = /^\d+$/;

  const handleInputChange = (fieldName: any, index) => (value: any) => {
    const valuesCopy = [...values]
    if (valuesCopy[index]) {
      valuesCopy[index][fieldName] = value
    } else {
      valuesCopy.push({
        [fieldName]: fieldName === 'globalId' ? Number(value) : value
      })
    }
    window.sessionStorage.setItem('attributes', JSON.stringify(valuesCopy))
    setValues(valuesCopy)
  }

  const resetError = (fieldName: string, index) => {
    const errorsCopy = [...errors]
    const validationErrorsCopy = [...validationErrors]
    if (errorsCopy[index]) {
      errorsCopy[index][fieldName] = false
    } else {
      errorsCopy[index] = { [fieldName]: false }
    }
    if (validationErrorsCopy[index]) {
      validationErrorsCopy[index][fieldName] = false
    } else {
      validationErrorsCopy[index] = { [fieldName]: false }
    }
    setErrors(errorsCopy)
    setValidationErrors(validationErrorsCopy)
  }

  const handleValidation = (regexp: RegExp, fieldName: 'description' | 'code' | 'globalId', index) => (value: string) => {
    let error = false
    const errorsCopy = [...validationErrors]

    if (value && regexp && !regexp.test(value || '')) {
      error = true
    }
    if (errorsCopy[index]) {
      errorsCopy[index][fieldName] = error
    } else {
      errorsCopy.push({
        [fieldName]: error
      })
    }
    setValidationErrors(errorsCopy)
  }

  const createAttributeMutation = useMutation({
    mutationFn: async () => {
      return axios.post(`${baseApiURL()}/attributes`, {
        attributes: values
      }, {
        headers: {
          Authorization: `Bearer ${state.user.accessToken}`
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attributes'] })
      window.sessionStorage.removeItem('attributes')
      close()
      toast('Attribute oprettet')
    },
    onError: (error: any) => {
      if (error.response?.data?.message?.startsWith('Key (code)=')) {
        const errorCopy = [...errors]
        const value = error.response.data.message.substring(error.response.data.message.lastIndexOf("=") + 2, error.response.data.message.lastIndexOf(") "))

        const errorIndex = values.findIndex(attribute => attribute.code === value)
        if (errorIndex >= 0) {
          if (errorCopy[errorIndex]) {
            errorCopy[errorIndex].code = true
          } else {
            errorCopy[errorIndex] = { code: true }
          }
          setErrors(errorCopy)
        }
      } else if (error.response?.data?.message?.startsWith('Key (global_id)=')) {
        const errorCopy = [...errors]
        const value = error.response.data.message.match(/\d+/)[0]
        const errorIndex = values.findIndex(attribute => Number(attribute.globalId) === Number(value))
        if (errorIndex >= 0) {
          if (errorCopy[errorIndex]) {
            errorCopy[errorIndex].globalId = true
          } else {
            errorCopy[errorIndex] = { globalId: true }
          }
          setErrors(errorCopy)
        }
      } else {
        if (error.response) {
          toast(error.response.data.message)
        } else {
          toast('Der gik desværre noget galt. Prøv igen om lidt')
        }
      }
    }
  })

  const createAttribute = () => {
    createAttributeMutation.mutate()
  }


  return (
    <div className={styles.addUser}>
      <header className={styles.header}>
        <h4>{values.length > 1 ? `Opret ${values.length} attributter` : 'Opret attribut'}</h4>
        <Button modifiers='tw-ml-auto' type='secondary' label='Ny attribut' handleClick={() => setValues([...values, {
          description: '',
          code: '',
          globalId: undefined
        }])} />
      </header>
      <div className={styles.container}>
        {values.map((value, index) => (
          <div key={index} className='tw-flex tw-mb-5'>
            <div className='tw-flex-2 tw-mr-3'>
              <Input
                label='Label navn'
                type='text'
                error={validationErrors[index] && validationErrors[index].description || errors[index] && errors[index].description}
                handleValidation={() => handleValidation(validString, 'description', index)}
                handleInputChange={() => handleInputChange('description', index)}
                resetError={() => resetError('description', index)}
                value={value.description}
                name='label name'
                placeholder='Fx. Cykel med'
              />
              {validationErrors[index] && validationErrors[index].description && <p className='errorMsg'>Navnet bør være mellem 2 og 32 karakterer. </p>}
            </div>
            <div className='tw-flex-1 tw-mr-3'>
              <Input
                label='Kode'
                type='text'
                value={value.code}
                error={validationErrors[index] && validationErrors[index].code || errors[index] && errors[index].code}
                handleValidation={() => handleValidation(validCode, 'code', index)}
                handleInputChange={() => handleInputChange('code', index)}
                resetError={() => resetError('code', index)}
                name='lastname'
                placeholder='Fx. 2C'
              />
              {validationErrors[index] && validationErrors[index].code && <p className='errorMsg'>Koden må maksimalt være 3 karakterer.</p>}
              {errors[index] && errors[index].code && <p className='errorMsg'>Koden er allerede i brug</p>}
            </div>
            <div className='tw-flex-1'>
              <Input
                label='Id'
                type='text'
                value={value.globalId ? value.globalId.toString() : ''}
                error={validationErrors[index] && validationErrors[index].globalId || errors[index] && errors[index].globalId}
                handleValidation={() => handleValidation(validId, 'globalId', index)}
                handleInputChange={() => handleInputChange('globalId', index)}
                resetError={() => resetError('globalId', index)}
                name='globalId'
                placeholder='Fx. 12'
              />
              {validationErrors[index] && validationErrors[index].globalId && <p className='errorMsg'>ID’et bør kun bestå af karakterer.</p>}
              {errors[index] && errors[index].globalId && <p className='errorMsg'>ID’et er allerede i brug</p>}
            </div>
          </div>
        ))}
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' handleClick={close} />
        <Button loading={createAttributeMutation.isPaused} disabled={(!values[0].description || !values[0].code || !values[0].globalId)} type='primary' label='Opret attribut' handleClick={createAttribute} />
      </footer>
    </div>
  )
}

export default AddAttribute
